import React, { useState } from 'react';
import { useChains } from '../coffee_clusters/hooks/useChains';
import { ChainList } from './components/ChainList';
import { ChainDetailPanel } from './components/ChainDetailPanel';
import { useChainMutations } from './hooks/useChainMutations';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from '../../ui';
import { toast } from 'react-toastify';

const ChainAdminPage: React.FC = () => {
    const [selectedChainId, setSelectedChainId] = useState<string | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newChainName, setNewChainName] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const { chains, loading, error, refetch } = useChains();
    const mutations = useChainMutations(refetch);

    const selectedChain = chains.find((c) => c._id === selectedChainId) || null;

    const handleCreateChain = async () => {
        if (!newChainName.trim()) return;
        setIsCreating(true);
        try {
            await mutations.create({ name: newChainName.trim() });
            toast.success('Chain created');
            setCreateDialogOpen(false);
            setNewChainName('');
        } catch {
            toast.error('Failed to create chain');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <>
            <style>{`
@media (max-width: 700px) {
    .chadm-page { padding: 12px 8px !important; }
    .chadm-grid-1 { grid-template-columns: 1fr !important; }
    .chadm-page h1 { font-size: 1.35rem !important; }
}
            `}</style>
            <div className="container mx-auto p-4 max-w-5xl chadm-page">
            <h1 className="text-2xl font-bold mb-4">Chain Admin</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 chadm-grid-1">
                <div className="lg:col-span-1">
                    <div className="rounded-lg border border-ink/15 p-4">
                        <h2 className="text-sm font-medium mb-3">Chains</h2>
                        <ChainList
                            chains={chains}
                            loading={loading}
                            error={error}
                            onRetry={refetch}
                            selectedId={selectedChainId}
                            onSelect={(c) => setSelectedChainId(c._id)}
                            onCreateClick={() => setCreateDialogOpen(true)}
                        />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ChainDetailPanel chain={selectedChain} onRefetchChains={refetch} />
                </div>
            </div>

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Create chain</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div>
                            <label className="text-sm font-medium mb-2 block">Name</label>
                            <Input
                                value={newChainName}
                                onChange={(e) => setNewChainName(e.target.value)}
                                placeholder="e.g. Mikel Coffee"
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateChain()}
                            />
                        </div>
                        <Button onClick={handleCreateChain} disabled={isCreating || !newChainName.trim()} className="w-full">
                            {isCreating ? 'Creating...' : 'Create'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            </div>
        </>
    );
};

export default ChainAdminPage;
